import React, { useState, useEffect } from "react";
import { TextField, Autocomplete, Box} from "@mui/material";
import { searchUsers } from "../services/usersApi";
import { useMsal } from '@azure/msal-react';
import { pwcEmailValidatorSync, UserSearchInfo } from "../services/common";

interface UserTextBoxProps {
    label: React.ReactNode;
    handleChange: (option: UserSearchInfo | null) => void;
    defaultValue: UserSearchInfo | null;
    disabled: boolean;
    handleValidChange: (isValid: boolean) => void;
    canBeEmpty: boolean;
}

const UserTextBox = React.forwardRef<HTMLDivElement, UserTextBoxProps>(
({ label, handleChange, defaultValue, disabled, handleValidChange, canBeEmpty, ...other}, ref) => {
        const { instance } = useMsal();
        const slotProps = { inputLabel: { shrink: true } };
        const [isWrong, setIsWrong] = useState(false);
        const [searchString, setSearchString] = useState<string | null>(null);
        const [userList, setUserList] = useState(defaultValue?.email == null ? [{ hint: 'Type to search' }] : [defaultValue]);
        const [errorMessage, setErrorMessage] = useState<string | null>(null);
        const onHandleChange = (e: UserSearchInfo) => {
            if (e?.email == defaultValue?.email) {
                return;
            }
            if (e?.isFreeTextValue === true)
            {
                const newElement = { email: e.email, name: e.email };
                setUserList([newElement]);
                handleChange(newElement);
                Validate(newElement);
            }
            else
            {
                handleChange(e);
                Validate(e);
            }
        };
        const Validate = (newValue: UserSearchInfo) => {
            if (canBeEmpty && ((!!newValue?.email) === false)) {
                setIsWrong(false);
                setErrorMessage(null);
                handleValidChange(true);
            }
            else {
                const errorMessage = pwcEmailValidatorSync(newValue?.email || '');
                const isValid = !errorMessage; // errorMessage is null if valid, string if invalid
                setIsWrong(!isValid);
                setErrorMessage(errorMessage);
                handleValidChange(isValid);
            }
        };
        const searchUsersDelay = async (searchString: string | null) => {
            if (searchString == null || searchString === '') {
                onHandleChange(null);
                return;
            }
            if (searchString.length > 2) {
                setSearchString(searchString);
            }
        };
        const onSearchUsers = async () => {
            if (searchString === null) {
                return;
            }
            if (searchString.length > 2) {
                const searchResult = await searchUsers(instance, searchString)
                setUserList(searchResult);
            }
        };
        React.useEffect(() => {
            const timeout = setTimeout(() => { onSearchUsers() }, 1000)
            return () => clearTimeout(timeout)
        }, [searchString]);

        useEffect(() => {
            Validate(defaultValue);
        }, []);
        return (
            <div ref={ref} style={{ display: 'inline-block' }} {...other}>
                { disabled ? (
                    <TextField className="details-apps-text" style={{ margin: '10px 5px', width: '370px' }} margin="dense"
                        label={label}
                        defaultValue={defaultValue?.name ?? defaultValue?.email}
                        error={isWrong}
                        helperText={isWrong ? errorMessage : ''}
                        disabled={disabled}
                        color={null}
                        focused={isWrong}
                        slotProps={slotProps}
                    />
                    ) : (
                    <Autocomplete
                        freeSolo={true}
                        selectOnFocus={true}
                        clearOnBlur={true}
                        handleHomeEndKeys={true}
                        onChange={(event, newValue) => {
                            if (typeof newValue === 'string') {
                                onHandleChange({ email: newValue, isFreeTextValue: true });
                            } else if (newValue && (!!newValue?.email)) {
                                onHandleChange(newValue);
                            }
                        }}
                        filterOptions={(options, params) => {
                            const result = [...options];

                            const { inputValue } = params;
                            const isExisting = options.some((option) => inputValue === option.email);
                            if (inputValue !== '' && !isExisting) {
                                result.push({
                                    email: inputValue,
                                    hint: `Use "${inputValue}" as owner`
                                });
                            }

                            return result;
                        }}
                        onInputChange={(e, v) => { searchUsersDelay(v); }}
                        disabled={false}
                        className="details-apps-autocomplete"
                        value={defaultValue}
                        getOptionLabel={(option) => option['name'] ?? option['email'] ?? ""}
                        options={userList}
                        renderOption={(props, option) => {
                            const { key, ...optionProps } = props;
                            return (
                                <Box
                                    key={key + option.email}
                                    component="li"
                                    sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                                    {...optionProps}
                                >
                                    <div>
                                        <div>{option.name ?? option.hint ?? option.email}</div>
                                        {(!!option.email) === true ? <div style={{ fontSize: '12px' }}>({option.email})</div> : ""}
                                    </div>
                                </Box>
                            );
                        }}
                        renderInput={(params) =>
                            <TextField {...params} slotProps={slotProps} className="details-apps-autocomplete-inner" margin="dense" label={label} error={isWrong} helperText={isWrong ? errorMessage : ''} value={defaultValue?.name ?? defaultValue?.email} />
                        }
                    />
                    )
                }
            </div>
        );
    }
);

export default UserTextBox;
